import { useApolloClient } from '@apollo/client';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowHeightParams,
  GridSelectionModel,
  MuiEvent,
} from '@mui/x-data-grid';
import { appRoutes } from 'app/app-routes';
import FavoriteIcons from 'common/components/FavoriteIcons';
import { imageHelper } from 'common/functions/image-helper';
import { formatters } from 'common/tools/formatters';
import { ListingStatus, ListingStatusValues } from 'common/types/listing-status';
import { LISTINGS_BY_USER_QUERY } from 'data/ListingsSearchQuery.graphql';
import { ListingImage, ListingsFindByUserQuery, User, UserFavorite } from 'graphql/generated';
import sellLogo from 'images/sell2.png';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsInfoFrame from 'stories/layout/WsInfoFrame';

interface ActionCol {
  listingId: string;
  user: User;
}

interface GridObj {
  id: string;
  images: ListingImage[];
  heading: string;
  price: string;
  status: string;
  dateAdded: string;
  actions: ActionCol;
}

const Favorites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();

  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const [skipEffect, skipEffectTo] = useState(false);
  const [favorites, favoritesTo] = useState<GridObj[]>([]);
  const [selectionModel, selectionModelTo] = useState<GridSelectionModel>([]);
  const gridHeight = 505;
  const gridWidth = 860;
  const imageWidth = 120;
  const rowHeight = (imageWidth - 20) * 0.75;

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'images',
      headerName: 'Image',
      width: imageWidth,
      editable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ListingImage[]>) => (
        <img
          style={{ width: '100%', height: 'auto' }}
          alt="car"
          src={imageHelper.getPrimaryImageLocation(params.value) as string}
        />
      ),
    },
    {
      field: 'heading',
      headerName: 'Heading',
      width: 180,
      editable: false,
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 80,
      editable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      editable: false,
    },
    {
      field: 'dateAdded',
      headerName: 'Date Added',
      width: 100,
      editable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      editable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ActionCol>) => (
        <FavoriteIcons
          listingId={params.value.listingId}
          value={true}
          deleteMode={true}
          user={params.value.user}
          toggleFn={skipEffectTo}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!skipEffect) {
      skipEffectTo(true);
      const loadFavorites = async (favorites: UserFavorite[]) => {
        const { data } = await client.query<ListingsFindByUserQuery>({
          query: LISTINGS_BY_USER_QUERY,
          variables: { filters: [{ operator: 'in', field: 'id', strVals: favorites.map(x => x.id) }] },
        });
        if (data?.listingsFind) {
          const listings = data.listingsFind;
          const objs: GridObj[] = [];
          for (const favorite of favorites) {
            const listing = listings.find(x => x.id === favorite.id);
            if (listing)
              objs.push({
                id: listing.id,
                dateAdded: moment(favorite.dateAdded).format('MMM DD, YYYY'),
                heading: listing.heading,
                status: ListingStatusValues[listing.status as ListingStatus],
                price: formatters.currency.format(listing.price),
                images: listing.images,
                actions: {
                  listingId: listing.id,
                  user: user!,
                },
              });
          }
          favoritesTo(_.orderBy(objs, ['dateAdded'], ['desc']));
        }
      };
      if (user?.favorites?.length) loadFavorites(user.favorites);
      else favoritesTo([]);
    }
  }, [dispatch, client, user, skipEffect]);

  const onCellClick = (params: GridCellParams, event: MuiEvent<React.MouseEvent>, details: GridCallbackDetails) => {
    if (params.field !== 'actions') navigate(`${appRoutes.itemDetail}/${params.id}/Favorites`);
  };

  const getRowHeight = (params: GridRowHeightParams) => rowHeight;

  return (
    <Grid container justifyContent="center" sx={{ flexGrow: 1 }}>
      <Grid container sx={{ width: gridWidth }}>
        <Grid item xs={12}>
          <Typography variant="h4" color="primary">
            Favorites
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <img src={sellLogo} alt="sell" width="100%" />
        </Grid>
        <Grid container item xs={12}>
          <Grid container justifyContent="center">
            <WsInfoFrame width="80%" title="Favorites" icon={<FavoriteBorderIcon color="secondary" />}>
              <>
                {favorites.length ? (
                  <Box sx={{ height: gridHeight, width: '100%' }}>
                    <DataGrid
                      sx={{
                        columnHeaderCheckbox: {
                          color: 'red',
                        },
                        '& .MuiDataGrid-columnHeaderTitleContainer .MuiCheckbox-root': {
                          color: 'red',
                          display: 'none',
                        },
                      }}
                      rows={favorites}
                      columns={columns}
                      getRowHeight={getRowHeight}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      columnVisibilityModel={{ id: false }}
                      // onRowClick={onRowClick}
                      onCellClick={onCellClick}
                      editMode="cell"
                      disableColumnFilter
                      disableColumnMenu
                      disableColumnSelector
                      disableDensitySelector
                      selectionModel={selectionModel}
                    />
                  </Box>
                ) : (
                  <WsFlexbox padding={4} centered>
                    You do not have any favorites.
                  </WsFlexbox>
                )}
              </>
            </WsInfoFrame>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Favorites;
